import { useEffect, useState } from "react";

import { ApiError, apiClient } from "../../api/client";
import type { Pillar } from "../../api/types";
import { UserPicker, type UserPickerValue } from "../../components/UserPicker";
import { UserRefChip } from "../../components/UserRefChip";
import { formatDate } from "../../lib/format";
import { useIdempotencyKey } from "../../lib/idempotencyKey";
import { useMemberRecipients } from "../../lib/useMemberRecipients";
import { pillarTint } from "./pillarColors";

type LoadStatus = "loading" | "ready" | "error";

const emptyPicker: UserPickerValue = { query: "", selectedID: "" };

// PillarsPage is the leadership CRUD list for company areas: create, rename,
// set or clear the executive sponsor, and toggle restricted/archived. There is
// no hard delete — archived is the terminal state, so existing blocks keep
// their pillar. Restricted pillars this viewer cannot see never arrive (the
// list is filtered server-side), but every blocks:pillars:manage holder also
// holds blocks:view_restricted, so managers see the full list.
export function PillarsPage() {
  const [pillars, setPillars] = useState<Pillar[]>([]);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [reloadKey, setReloadKey] = useState(0);

  const [createName, setCreateName] = useState("");
  const [createSponsor, setCreateSponsor] = useState<UserPickerValue>(emptyPicker);
  const [createRestricted, setCreateRestricted] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editSponsor, setEditSponsor] = useState<UserPickerValue>(emptyPicker);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [toggleError, setToggleError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const members = useMemberRecipients(true);
  // The server replays a stored answer for a reused key, so a refused create,
  // edit or toggle needs a new key to be retried; a network error may never
  // have reached the server, so that retry keeps the key.
  const createKey = useIdempotencyKey();
  const editKey = useIdempotencyKey();
  const toggleKey = useIdempotencyKey();

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    apiClient
      .listPillars()
      .then((result) => {
        if (cancelled) {
          return;
        }
        setPillars(result);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  async function handleCreate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const name = createName.trim();
    if (name === "" || creating) {
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      await apiClient.createPillar(
        {
          name,
          ...(createSponsor.selectedID === "" ? {} : { sponsor_user_id: createSponsor.selectedID }),
          ...(createRestricted ? { restricted: true } : {})
        },
        createKey.keyFor(`create:${name}:${createSponsor.selectedID}:${String(createRestricted)}`)
      );
      setCreateName("");
      setCreateSponsor(emptyPicker);
      setCreateRestricted(false);
      createKey.reset();
      setReloadKey((key) => key + 1);
    } catch (caught) {
      if (caught instanceof ApiError) {
        createKey.reset();
      }
      setCreateError(messageFor(caught, "Couldn't create pillar."));
    } finally {
      setCreating(false);
    }
  }

  function startEdit(pillar: Pillar) {
    setEditingId(pillar.id);
    setEditName(pillar.name);
    setEditSponsor(
      pillar.sponsor === null
        ? emptyPicker
        : {
            query: pillar.sponsor.name.trim() === "" ? pillar.sponsor.email : pillar.sponsor.name,
            selectedID: pillar.sponsor.id
          }
    );
    setEditError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName("");
    setEditSponsor(emptyPicker);
    setEditError(null);
  }

  async function handleEditSubmit(event: React.FormEvent<HTMLFormElement>, pillar: Pillar) {
    event.preventDefault();
    const name = editName.trim();
    if (name === "" || editSubmitting) {
      return;
    }
    const payload: { name?: string; sponsor_user_id?: string | null } = {};
    if (name !== pillar.name) {
      payload.name = name;
    }
    const sponsorBefore = pillar.sponsor?.id ?? "";
    if (editSponsor.selectedID !== sponsorBefore) {
      payload.sponsor_user_id = editSponsor.selectedID === "" ? null : editSponsor.selectedID;
    }
    if (Object.keys(payload).length === 0) {
      cancelEdit();
      return;
    }
    setEditSubmitting(true);
    setEditError(null);
    try {
      await apiClient.updatePillar(pillar.id, payload, editKey.keyFor(`edit:${pillar.id}:${JSON.stringify(payload)}`));
      editKey.reset();
      cancelEdit();
      setReloadKey((key) => key + 1);
    } catch (caught) {
      if (caught instanceof ApiError) {
        editKey.reset();
      }
      setEditError(messageFor(caught, "Couldn't update pillar."));
    } finally {
      setEditSubmitting(false);
    }
  }

  // Restricted/archived flip with a plain button — both are reversible, so no
  // confirm modal. Flipping restricted on instantly hides the pillar's whole
  // block subtree from IC viewers.
  async function toggle(pillar: Pillar, field: "restricted" | "archived") {
    if (togglingId !== null) {
      return;
    }
    setTogglingId(pillar.id);
    setToggleError(null);
    const next = field === "restricted" ? !pillar.restricted : !pillar.archived;
    try {
      await apiClient.updatePillar(
        pillar.id,
        { [field]: next },
        toggleKey.keyFor(`toggle:${pillar.id}:${field}:${String(next)}`)
      );
      toggleKey.reset();
      setReloadKey((key) => key + 1);
    } catch (caught) {
      if (caught instanceof ApiError) {
        toggleKey.reset();
      }
      setToggleError(messageFor(caught, "Couldn't update pillar."));
    } finally {
      setTogglingId(null);
    }
  }

  return (
    <div className="blocks-view">
      <section className="card">
        <h2>Create pillar</h2>
        <form onSubmit={handleCreate} className="form-row form-row--inline">
          <label>
            Name
            <input
              type="text"
              value={createName}
              maxLength={100}
              onChange={(event) => setCreateName(event.target.value)}
              placeholder="Commercial"
              required
            />
          </label>
          <UserPicker
            label="Executive sponsor"
            placeholder="Type a name or email…"
            value={createSponsor}
            recipients={members.recipients}
            loadStatus={members.status}
            loadErrorMessage={members.errorMessage}
            loadingMessage="Loading members…"
            emptyMessage="No members in your tenant yet."
            noSelectionHint="Optional — pick a person or leave empty."
            onChange={setCreateSponsor}
          />
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={createRestricted}
              onChange={(event) => setCreateRestricted(event.target.checked)}
            />
            Restricted
          </label>
          <button type="submit" className="btn btn--primary" disabled={creating || createName.trim() === ""}>
            {creating ? "Creating…" : "Create"}
          </button>
        </form>
        {createError !== null && <p className="form-row__error">{createError}</p>}
        <p className="muted">
          A restricted pillar is hidden everywhere from ICs and never appears in the Vision statue — for anyone.
        </p>
      </section>

      <section className="card">
        <h2>Pillars</h2>
        {status === "loading" && <p className="loading">Loading…</p>}
        {status === "error" && (
          <p className="form-row__error">
            Couldn't load pillars.{" "}
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setReloadKey((key) => key + 1)}>
              Retry
            </button>
          </p>
        )}
        {status === "ready" && pillars.length === 0 && (
          <p className="muted">No pillars yet — create one above. Projects need a pillar.</p>
        )}
        {toggleError !== null && <p className="form-row__error">{toggleError}</p>}
        {status === "ready" && pillars.length > 0 && (
          <table className="ledger-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Executive sponsor</th>
                <th>Flags</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pillars.map((pillar) => (
                <tr key={pillar.id}>
                  {editingId === pillar.id ? (
                    <td colSpan={5}>
                      <form
                        className="form-row form-row--inline"
                        onSubmit={(event) => void handleEditSubmit(event, pillar)}
                      >
                        <input
                          type="text"
                          value={editName}
                          maxLength={100}
                          onChange={(event) => setEditName(event.target.value)}
                          aria-label="Pillar name"
                          autoFocus
                        />
                        <UserPicker
                          label="Executive sponsor"
                          placeholder="Type a name or email…"
                          value={editSponsor}
                          recipients={members.recipients}
                          loadStatus={members.status}
                          loadErrorMessage={members.errorMessage}
                          loadingMessage="Loading members…"
                          emptyMessage="No members in your tenant yet."
                          noSelectionHint="Clear to remove the sponsor."
                          onChange={setEditSponsor}
                        />
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={cancelEdit}
                          disabled={editSubmitting}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn--primary btn--sm"
                          disabled={editName.trim() === "" || editSubmitting}
                        >
                          {editSubmitting ? "Saving…" : "Save"}
                        </button>
                        {editError !== null && <p className="form-row__error">{editError}</p>}
                      </form>
                    </td>
                  ) : (
                    <>
                      <td>
                        <span
                          className="pillar-name pillar-name--tinted"
                          style={{ "--pillar-tint": pillarTint(pillar.id, pillar.name) } as React.CSSProperties}
                        >
                          {pillar.name}
                        </span>
                      </td>
                      <td>{pillar.sponsor !== null ? <UserRefChip user={pillar.sponsor} /> : <span className="muted">—</span>}</td>
                      <td>
                        {pillar.restricted && <span className="chip">Restricted</span>}
                        {pillar.archived && <span className="chip">Archived</span>}
                        {!pillar.restricted && !pillar.archived && <span className="muted">—</span>}
                      </td>
                      <td>{formatDate(pillar.created_at)}</td>
                      <td className="row-actions">
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => startEdit(pillar)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => void toggle(pillar, "restricted")}
                          disabled={togglingId !== null}
                        >
                          {pillar.restricted ? "Unrestrict" : "Restrict"}
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => void toggle(pillar, "archived")}
                          disabled={togglingId !== null}
                        >
                          {pillar.archived ? "Unarchive" : "Archive"}
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function messageFor(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return caught instanceof Error ? caught.message : fallback;
}
